import { useState } from 'react';
import { PenTool, FileText } from 'lucide-react';
import { usePdfSigningBatches } from '../hooks/useApi';
import { DataTable } from '../components/shared/DataTable';
import { StatusBadge } from '../components/shared/StatusBadge';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import type { SigningBatchInfo } from '../types/api';

function batchStatusVariant(status: number): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  switch (status) {
    case 0: return 'neutral';    // Pending
    case 1: return 'info';       // Processing
    case 2: return 'success';    // Completed
    case 3: return 'danger';     // Failed
    case 4: return 'warning';    // PartiallyCompleted
    case 5: return 'warning';    // RateLimited
    default: return 'neutral';
  }
}

export function SigningPage() {
  const [hours, setHours] = useState(168);
  const pdf = usePdfSigningBatches(hours);

  const batchColumns = [
    {
      key: 'organisationName',
      header: 'Organizacja',
      sortable: true,
      render: (row: SigningBatchInfo) => row.organisationName || 'Globalna',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row: SigningBatchInfo) => (
        <StatusBadge label={row.statusLabel} variant={batchStatusVariant(row.status)} pulse={row.status === 1} />
      ),
    },
    { key: 'totalItems', header: 'Razem', sortable: true },
    {
      key: 'completedItems',
      header: 'Zakonczone',
      render: (row: SigningBatchInfo) => <span className="text-emerald-600 dark:text-emerald-300">{row.completedItems}</span>,
    },
    {
      key: 'failedItems',
      header: 'Bledy',
      render: (row: SigningBatchInfo) =>
        row.failedItems > 0 ? <span className="text-red-600 dark:text-red-300 font-medium">{row.failedItems}</span> : <span className="text-gray-400 dark:text-gray-600">0</span>,
    },
    {
      key: 'processingStartedDate',
      header: 'Rozpoczeto',
      sortable: true,
      render: (row: SigningBatchInfo) =>
        row.processingStartedDate ? new Date(row.processingStartedDate).toLocaleString('pl-PL') : '-',
    },
    {
      key: 'completedDate',
      header: 'Zakonczone',
      sortable: true,
      render: (row: SigningBatchInfo) =>
        row.completedDate ? new Date(row.completedDate).toLocaleString('pl-PL') : '-',
    },
    {
      key: 'errorMessage',
      header: 'Blad',
      render: (row: SigningBatchInfo) =>
        row.errorMessage ? (
          <span className="text-xs text-red-500 dark:text-red-300/80 max-w-xs truncate block" title={row.errorMessage}>
            {row.errorMessage}
          </span>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <PenTool className="h-6 w-6 text-brand-600 dark:text-brand-400" />
            Podpisywanie
          </h1>
          <p className="text-sm text-gray-500 mt-1">Batche podpisywania PDF (PAdES) przez EuroCert</p>
        </div>
        <select value={hours} onChange={(e) => setHours(Number(e.target.value))} className="input-field text-sm">
          <option value={24}>Ostatnie 24h</option>
          <option value={72}>Ostatnie 3 dni</option>
          <option value={168}>Ostatni tydzien</option>
          <option value={720}>Ostatni miesiac</option>
        </select>
      </div>

      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-4">
          <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          Batche podpisywania PDF
        </h2>
        {pdf.isLoading ? (
          <LoadingSpinner size="sm" />
        ) : pdf.error ? (
          <ErrorAlert message={pdf.error.message} onRetry={() => pdf.refetch()} />
        ) : pdf.data ? (
          <DataTable columns={batchColumns} data={pdf.data} compact emptyMessage="Brak batchy w wybranym okresie" />
        ) : null}
      </div>
    </div>
  );
}
